/**
 * 静态路由配置
 * 包含登录页、异常页面等不需要权限控制的页面
 *
 * 更新记录：
 * - 2025-11-01: 使用模块化导入方式，从 @/modules/* 导入组件
 * - 2025-11-07: 添加Suspense包裹解决懒加载组件错误
 * - 2025-11-20: 添加Storybook组件库页面路由
 * - 2025-11-29: 统一登录页面，使用单一Login组件
 */

import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
// 统一登录页面
const Login = lazy(() => import('@/pages/tenant/Login'));

// 平台管理端登录页面
const PlatformLogin = lazy(() => import('@/pages/Platform/Login'));

// REQ-20260508-0001 #10180: 多租户选择页
const TenantSelect = lazy(() => import('@/pages/TenantSelect'));
// REQ-20260508-0001 #10181: 门户选择页（双 portal 用户）
const PortalSelect = lazy(() => import('@/pages/PortalSelect'));

// 从Shared模块导入异常页面
const NotFound = lazy(() => import('@/modules/shared/pages/Exception/NotFound'));
const Forbidden = lazy(() => import('@/modules/shared/pages/Exception/Forbidden'));
const ServerError = lazy(() => import('@/modules/shared/pages/Exception/ServerError'));
// REQ-20260508-0001 #10183: 门户越权拦截页
const PortalForbidden = lazy(() => import('@/modules/shared/pages/Exception/PortalForbidden'));

// 组件库页面（Storybook集成）
const ComponentLibrary = lazy(() => import('@/pages/System/ComponentLibrary'));
// 对外接口文档（公开访问）
const OpenApiDocs = lazy(() => import('@/pages/OpenApiDocs'));

// 操作手册页面（无需登录）
const GuideIndex = lazy(() => import('@/pages/Guide'));
const BrandGuideIndex = lazy(() => import('@/pages/Guide/Brand'));
const BrandGettingStarted = lazy(() => import('@/pages/Guide/Brand/GettingStarted'));
const BrandProductManage = lazy(() => import('@/pages/Guide/Brand/ProductManage'));
const BrandOrderManage = lazy(() => import('@/pages/Guide/Brand/OrderManage'));
const BrandCustomerManage = lazy(() => import('@/pages/Guide/Brand/CustomerManage'));
const BrandWarehouseManage = lazy(() => import('@/pages/Guide/Brand/WarehouseManage'));
const BrandFinanceManage = lazy(() => import('@/pages/Guide/Brand/FinanceManage'));
const BrandPromotionLogistics = lazy(() => import('@/pages/Guide/Brand/PromotionLogistics'));
const BrandSystemManage = lazy(() => import('@/pages/Guide/Brand/SystemManage'));
const CustomerGuideIndex = lazy(() => import('@/pages/Guide/Customer'));
const CustomerGettingStarted = lazy(() => import('@/pages/Guide/Customer/GettingStarted'));
const CustomerOrderFlow = lazy(() => import('@/pages/Guide/Customer/OrderFlow'));
const CustomerCustomerManage = lazy(() => import('@/pages/Guide/Customer/CustomerManage'));
const CustomerQuoteTrade = lazy(() => import('@/pages/Guide/Customer/QuoteTrade'));
const CustomerWarehouseManage = lazy(() => import('@/pages/Guide/Customer/WarehouseManage'));
const CustomerOrgMembers = lazy(() => import('@/pages/Guide/Customer/OrgMembers'));
const CustomerTaskManage = lazy(() => import('@/pages/Guide/Customer/TaskManage'));
const CustomerFinanceManage = lazy(() => import('@/pages/Guide/Customer/FinanceManage'));
const CustomerLogistics = lazy(() => import('@/pages/Guide/Customer/Logistics'));

// 统一的加载中组件
// 本文件为路由配置模块（导出 staticRoutes 数组），非组件模块，Fast Refresh 不适用；
// LoadingFallback 仅在本文件内使用，无需 HMR 单独更新。
// eslint-disable-next-line react-refresh/only-export-components
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
  }}>
    <Spin size="large" tip="加载中..."><div style={{ minHeight: 100 }} /></Spin>
  </div>
);

// 包裹懒加载组件的Suspense高阶函数
const withSuspense = (Component: React.LazyExoticComponent<React.FC>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

export const staticRoutes: RouteObject[] = [
  // cool2.0 推广工具旧路径兼容
  {
    path: '/productOption',
    element: <Navigate to="/tenant/productOption" replace />,
  },
  {
    path: '/prd_combination_formulate',
    element: <Navigate to="/tenant/prd_combination_formulate" replace />,
  },
  {
    path: '/prd_combination_audit',
    element: <Navigate to="/tenant/prd_combination_audit" replace />,
  },
  // 统一登录页面
  {
    path: '/login',
    element: withSuspense(Login),
  },
  // 兼容旧路径 - 重定向到统一登录
  {
    path: '/auth/login',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/tenant/login',
    element: <Navigate to="/login" replace />,
  },

  // 平台管理端登录页面
  {
    path: '/platform/login',
    element: withSuspense(PlatformLogin),
  },

  // REQ-20260508-0001 #10180: 多租户选择页（登录后中间态）
  {
    path: '/tenant-select',
    element: withSuspense(TenantSelect),
  },

  // REQ-20260508-0001 #10181: 门户选择页（双 portal 用户）
  {
    path: '/portal-select',
    element: withSuspense(PortalSelect),
  },

  // 对外接口文档：公开路由，不经过 AuthRoute
  {
    path: '/open-api',
    element: withSuspense(OpenApiDocs),
  },

  // 组件库页面（Storybook）
  // 注意：此路由需要登录后访问，但为了开发方便暂时放在静态路由中
  // 生产环境建议移到动态路由或添加权限控制
  {
    path: '/system/component-library',
    element: withSuspense(ComponentLibrary),
  },

  // 操作手册（无需登录的静态页面）
  {
    path: '/guide',
    element: withSuspense(GuideIndex),
  },
  {
    path: '/guide/brand',
    element: withSuspense(BrandGuideIndex),
    children: [
      { index: true, element: <Navigate to="/guide/brand/getting-started" replace /> },
      { path: 'getting-started', element: withSuspense(BrandGettingStarted) },
      { path: 'product-manage', element: withSuspense(BrandProductManage) },
      { path: 'order-manage', element: withSuspense(BrandOrderManage) },
      { path: 'customer-manage', element: withSuspense(BrandCustomerManage) },
      { path: 'warehouse-manage', element: withSuspense(BrandWarehouseManage) },
      { path: 'finance-manage', element: withSuspense(BrandFinanceManage) },
      { path: 'promotion-logistics', element: withSuspense(BrandPromotionLogistics) },
      { path: 'system-manage', element: withSuspense(BrandSystemManage) },
    ],
  },
  {
    path: '/guide/customer',
    element: withSuspense(CustomerGuideIndex),
    children: [
      { index: true, element: <Navigate to="/guide/customer/getting-started" replace /> },
      { path: 'getting-started', element: withSuspense(CustomerGettingStarted) },
      { path: 'org-members', element: withSuspense(CustomerOrgMembers) },
      { path: 'order-flow', element: withSuspense(CustomerOrderFlow) },
      { path: 'customer-manage', element: withSuspense(CustomerCustomerManage) },
      { path: 'quote-trade', element: withSuspense(CustomerQuoteTrade) },
      { path: 'task-manage', element: withSuspense(CustomerTaskManage) },
      { path: 'finance-manage', element: withSuspense(CustomerFinanceManage) },
      { path: 'warehouse-manage', element: withSuspense(CustomerWarehouseManage) },
      { path: 'logistics', element: withSuspense(CustomerLogistics) },
    ],
  },

  // 异常页面
  {
    path: '/exception',
    children: [
      {
        path: '404',
        element: withSuspense(NotFound),
      },
      {
        path: '403',
        element: withSuspense(Forbidden),
      },
      {
        path: '500',
        element: withSuspense(ServerError),
      },
      {
        // REQ-20260508-0001 #10183: 门户越权拦截
        path: 'portal-forbidden',
        element: withSuspense(PortalForbidden),
      },
    ],
  },
];
